//@ts-nocheck
'use client';
import React, { ReactNode, FC, useEffect, useMemo, useState } from 'react';

import { mockBundlerGroups } from '@containers/Bundler/Mocks';
import { v4 as uuid } from 'uuid';
import {
    Bundle,
    BundleBuilderState,
    BundlerProviderProps,
    Collection,
    PrismicBundleDefinition,
    Product,
    Variant,
} from '../Types';
import { useParams } from 'next/navigation';
import useCart from '@app/[lang]/cart/state';
import { Language } from '@app/lib/store-context/Context';
import { getShopifyClientSettings } from '@app/lib/shopify/get-client-settings';
import getCartId from '@app/lib/shopify/checkout/get-cart-id';
import setCartId from '@app/lib/shopify/checkout/set-cart-id';
import { getClient } from '@app/lib/shopify';
import bundlerCollectionByHandle from '@app/lib/shopify/queries/bundler-collection-by-handle';
import addLinesToCart from '@app/lib/shopify/methods/add-lines-to-cart';
import removeLineItems from '@app/lib/shopify/methods/remove-from-cart';
import useUI from '@app/components/ui/state';
import BundleDefinition from '@containers/Bundler/Types/BundleDefinition';
import { handleUserErrors } from '@app/lib/helpers/handle-user-errors';
import Cart from '@app/lib/types/Cart';
import getGenderLineAttribute from '@app/lib/shopify/cart/get-gender-line-attribute';
import { getListAttributionLineAttributes } from '@app/lib/shopify/cart/list-attribution';

const emptyBundlerState: BundleBuilderState = {
    bundle: null,
    isFetching: true,
    selectedProduct: null,
    selectedCollectionKey: null,
    selectedCollectionHandle: null,
    fetchedCollections: [],
    chosenBundleVariants: {},
    selectedCollection: null,
    bundleSize: 0,
    bundleId: null,
    errorMessage: null,
};

export const BundlerContext = React.createContext<BundleBuilderState | any>(
    emptyBundlerState
);

const addUUIDs = (bundleDefinition) => {
    return bundleDefinition.map((bundle) => {
        return {
            ...bundle,
            collections: bundle.collections.map((collection) => {
                return {
                    ...collection,
                    key: uuid(),
                };
            }),
        };
    });
};

export const BundlerProvider: FC<BundlerProviderProps> = (props) => {
    const [bundlerState, setBundlerState] = useState(emptyBundlerState);
    const { lang } = useParams();
    const { toggleUI } = useUI();
    const { setCart, cart } = useCart();
    const bundleDefinition = addUUIDs(
        props.bundleDefinition || mockBundlerGroups
    );
    let initialBundlerState: BundleBuilderState = emptyBundlerState;
    let selections = null;

    const clientSettings = getShopifyClientSettings(lang as Language);

    useEffect(() => {
        const loadState = async () => {
            let stateUpdates: { [key: string]: any } = {};
            const getSelectedBundleGroup = (definition: BundleDefinition) => {
                // TODO: Reinstate.
                // if (typeof query.bundle === 'string') {
                //     return bundleDefinition.find(
                //         (bundle) => bundle.handle === query.bundle
                //     );
                // }

                return definition[0];
            };

            const defaultGroup = getSelectedBundleGroup(bundleDefinition);
            initialBundlerState = {
                ...emptyBundlerState,
                bundle: defaultGroup,
                selectedCollectionKey: defaultGroup.collections[0].key,
                selectedCollectionHandle: defaultGroup.collections[0].handle,
                bundleSize: defaultGroup.collections.length,
            };

            if (!selections) {
                setBundlerState({
                    ...initialBundlerState,
                    isFetching: false,
                });
            }
        };
        loadState().catch((error) =>
            console.log(
                'loading state with optional pre selections via URL',
                error
            )
        );
    }, []);

    const getVariantNumber = (variantId: string): number => {
        return parseInt(variantId.split('gid://shopify/ProductVariant/')[1]);
    };

    const getBundleScriptPublicHash = (variantId) => {
        return (
            Number(process.env.NEXT_PUBLIC_BUNDLE_SCRIPT_PUBLIC_KEY) + variantId
        );
    };

    const getBundlerVariantLineItems = () => {
        let lineItems = [];

        Object.entries(bundlerState.chosenBundleVariants).forEach(
            ([key, variant], index) => {
                const { bundle } = bundlerState;
                if (!bundle) return;
                const { discountPercentage } = bundle;
                const variantNumber = getVariantNumber(variant.id);
                const collectionHandle = bundlerState.bundle.collections.find(
                    (collection) => collection.key === key
                )?.handle;
                const discountValue =
                    variantNumber +
                    Number(process.env.NEXT_PUBLIC_BUNDLE_SCRIPT_PUBLIC_KEY) +
                    Number(discountPercentage);
                lineItems.push({
                    merchandiseId: variant.id,
                    quantity: 1,
                    attributes: [
                        ...getGenderLineAttribute(variant.product),
                        ...getListAttributionLineAttributes({
                            itemListId: bundle.handle,
                            itemListName: bundle.title,
                            index,
                        }),
                        {
                            key: '_item_identifier',
                            value: `${uuid()}`,
                        },
                        {
                            key: '_discount_value',
                            value: `${discountValue}`,
                        },
                        {
                            key: '_discount_verification',
                            value: `${getBundleScriptPublicHash(
                                variantNumber
                            )}`,
                        },
                        {
                            key: '_bundle_id',
                            value: `${bundlerState.bundleId}`,
                        },
                        {
                            key: '_bundle_title',
                            value: bundle.title,
                        },
                        {
                            key: '_bundle_handle',
                            value: bundle.handle,
                        },
                        {
                            key: '_bundle_validation',
                            value: `${bundlerState.bundleId}:${bundle.handle}:${collectionHandle}`,
                        },
                    ],
                });
            }
        );

        return lineItems;
    };

    const addBundleToCart = async () => {
        setBundlerState((prevState) => ({
            ...prevState,
            errorMessage: null,
        }));

        const cartID = getCartId(lang as Language);
        const clientSettings = getShopifyClientSettings(lang as Language);
        const bundlerVariantLineItems = getBundlerVariantLineItems();
        const response = await addLinesToCart(
            clientSettings,
            cartID,
            bundlerVariantLineItems
        );

        const { cart, userErrors = [] } = response;

        // If any errors (e.g. stock unavailability) come back from the server, we want to prevent the bundle being added to the cart and show the message
        const errorMessages = [];
        if (userErrors.length > 0) {
            handleUserErrors(userErrors, (field, message) => {
                errorMessages.push(message);
            });

            setBundlerState((prevState) => ({
                ...prevState,
                bundleId: uuid(),
                errorMessage: errorMessages.join(', '),
            }));

            if (cart) {
                const addedLineIds = cart.lines.edges
                    .filter((edge) =>
                        edge.node.attributes.some(
                            (attr) =>
                                attr.key === '_bundle_id' &&
                                attr.value === bundlerState.bundleId
                        )
                    )
                    .map((edge) => edge.node.id);

                if (addedLineIds.length > 0) {
                    const updatedCart = await removeLineItems(
                        cartID,
                        addedLineIds,
                        clientSettings
                    );
                    if (updatedCart) {
                        setCart(updatedCart as Cart);
                    }
                }
                return;
            }
        } else {
            if (cart) {
                setCart(cart);
                setCartId(lang as Language, cart.id);
                toggleUI('cart');
                setBundlerState((prevState) => ({
                    ...prevState,
                    bundleId: uuid(),
                    errorMessage: null,
                }));
            }
        }
    };

    const reset = () => {
        setBundlerState(initialBundlerState);
    };

    const setBundle = (bundle: Bundle) => {
        setBundlerState({
            ...initialBundlerState,
            bundle: bundle,
            selectedCollectionHandle: bundle.collections[0].handle,
            selectedCollectionKey: bundle.collections[0].key,
        });
    };

    const regenerateBundleId = () => {
        const ID = uuid();
        setBundlerState({
            ...bundlerState,
            bundleId: ID,
        });
    };

    const setIsFetching = (isFetching: boolean) => {
        setBundlerState({
            ...bundlerState,
            isFetching: isFetching,
        });
    };

    const setSelectedProduct = (product: Product) => {
        setBundlerState({
            ...bundlerState,
            selectedProduct: product,
        });
    };

    const setSelectedCollection = (collection: Collection) => {
        setBundlerState({
            ...bundlerState,
            selectedCollection: collection,
        });
    };

    const setSelectedCollectionHandle = (handle: string) => {
        setBundlerState({
            ...bundlerState,
            selectedCollectionHandle: handle,
        });
    };

    const setSelectedCollectionKey = (key: string) => {
        if (!bundlerState?.bundle?.collections) return;

        const handle = bundlerState.bundle.collections.find(
            (collection) => collection.key === key
        )?.handle;

        if (!handle) return;

        setBundlerState({
            ...bundlerState,
            selectedCollectionKey: key,
            selectedCollectionHandle: handle,
        });
    };

    const getCollectionsSortedBySelection = () => {
        const collectionHasBeenChosen: any = (handle: string) =>
            !!bundlerState.chosenBundleVariants[handle];
        if (!bundlerState.bundle) return [];
        return bundlerState.bundle.collections.sort(function (a, b) {
            return (
                collectionHasBeenChosen(b.handle) -
                collectionHasBeenChosen(a.handle)
            );
        });
    };

    const getNextCollection = () => {
        if (!bundlerState.bundle) return null;
        const unselectedCollections = bundlerState.bundle.collections.filter(
            (collection) => {
                return (
                    Object.keys(bundlerState.chosenBundleVariants).indexOf(
                        collection.key
                    ) === -1
                );
            }
        );

        if (unselectedCollections[0]) {
            return unselectedCollections[0];
        }

        const nextCollectionPosition =
            bundlerState.bundle.collections.findIndex(
                (bundle) => bundle.key === bundlerState.selectedCollectionKey
            ) + 1;

        return bundlerState.bundle.collections[nextCollectionPosition];
    };

    useEffect(() => {
        // Go to next unselected collection when items are added to the bundle
        if (Object.keys(bundlerState.chosenBundleVariants).length === 0) return;
        let nextCollection = getNextCollection();
        if (nextCollection) {
            const updatedState: BundleBuilderState = {
                ...bundlerState,
                selectedCollectionKey: nextCollection.key,
                selectedCollectionHandle: nextCollection.handle,
            };

            setBundlerState(updatedState);
        }
    }, [bundlerState.chosenBundleVariants]);

    const addVariantToBundle = (variant: Variant) => {
        const { chosenBundleVariants, selectedCollectionKey } = bundlerState;
        if (!selectedCollectionKey) return;
        const newBundleVariants = Object.assign({}, chosenBundleVariants);
        newBundleVariants[selectedCollectionKey] = variant;
        const bundleId = uuid();
        const updatedState: BundleBuilderState = {
            ...bundlerState,
            chosenBundleVariants: newBundleVariants,
            bundleId,
        };
        setBundlerState(updatedState);
    };

    const getSelectedCollection = () => {
        const { selectedCollectionHandle, fetchedCollections } = bundlerState;
        const selectedCollection = fetchedCollections.find(
            (collection) => collection.handle === selectedCollectionHandle
        );
        if (selectedCollection) {
            setSelectedCollection(selectedCollection);
            return selectedCollection;
        }
        return null;
    };

    const getChosenProductHandleForCollection = (collectionKey: string) => {
        if (!bundlerState.chosenBundleVariants[collectionKey]) return null;

        return bundlerState.chosenBundleVariants[collectionKey].product.handle;
    };

    const getSelectedProductForCollection = (bundleCollection: Collection) => {
        const { selectedCollectionKey } = bundlerState;

        if (!selectedCollectionKey) return null;

        // Prefer the first in-stock product so the detail panel never opens
        // on a product that is hidden from the (sold-out-filtered) picker.
        const availableEdges = bundleCollection?.products?.edges?.filter(
            (edge) =>
                edge?.node?.variants?.edges?.some(
                    (variant) => variant?.node?.availableForSale
                )
        );

        let firstProductInCollection =
            availableEdges?.[0]?.node ??
            bundleCollection?.products?.edges?.[0]?.node;

        const chosenProductHandle = getChosenProductHandleForCollection(
            selectedCollectionKey
        );

        if (chosenProductHandle) {
            const chosenProduct = bundleCollection?.products?.edges?.find(
                (edge) => edge.node.handle === chosenProductHandle
            );

            if (chosenProduct) {
                firstProductInCollection = chosenProduct.node;
            }
        }

        return firstProductInCollection;
    };

    useEffect(() => {
        const { selectedCollectionHandle } = bundlerState;

        if (!selectedCollectionHandle) return;

        const cachedCollection = getSelectedCollection();

        if (cachedCollection) {
            const defaultProductSelection =
                getSelectedProductForCollection(cachedCollection);

            setBundlerState({
                ...bundlerState,
                selectedCollection: cachedCollection,
                selectedProduct: defaultProductSelection,
            });
            return;
        }

        const fetchCollectionIfNeeded = async () => {
            setIsFetching(true);
            let bundleCollection = null;
            try {
                const collectionRes = await getClient(
                    clientSettings
                ).request.send({
                    query: bundlerCollectionByHandle,
                    variables: {
                        country: clientSettings.shopifyMarketCountryCode,
                        handle: selectedCollectionHandle,
                        first: 250,
                    },
                });
                bundleCollection = collectionRes?.collection;
            } catch (e) {
                console.log(
                    `Couldn\t fetch collection for "${selectedCollectionHandle}"`,
                    e
                );
            }

            const defaultProductSelection =
                getSelectedProductForCollection(bundleCollection);

            setBundlerState({
                ...bundlerState,
                selectedCollection: bundleCollection,
                selectedProduct: defaultProductSelection,
                fetchedCollections: [
                    bundleCollection,
                    ...bundlerState.fetchedCollections,
                ],
                isFetching: false,
            });
        };

        fetchCollectionIfNeeded().catch((error) => {
            console.log(
                `Couldn\'t fetch collection [${selectedCollectionHandle}] because ... `,
                error
            );
            setIsFetching(false);
        });
    }, [bundlerState.selectedCollectionKey]);

    const assertBundleIsComplete = () => {
        const { bundle, chosenBundleVariants } = bundlerState;
        if (!bundle) return false;
        return (
            Object.keys(chosenBundleVariants).length ===
            bundle.collections.length
        );
    };

    const value = useMemo(
        () => ({
            ...bundlerState,
            bundleDefinition,
            setBundle,
            reset,
            setSelectedProduct,
            setSelectedCollectionKey,
            setSelectedCollectionHandle,
            getCollectionsSortedBySelection,
            addVariantToBundle,
            getSelectedCollection,
            addBundleToCart,
            assertBundleIsComplete,
        }),
        [bundlerState]
    );

    return <BundlerContext.Provider value={value} {...props} />;
};

export const useBundler = () => {
    const context = React.useContext(BundlerContext);
    if (context === undefined) {
        throw new Error(`useBundler must be used within the Bundler`);
    }
    return context;
};

type MigratedLegacyBundlerProviderProps = {
    bundleDefinition: PrismicBundleDefinition;
    children?: ReactNode;
};

export const MigratedBundlerProvider: FC<
    MigratedLegacyBundlerProviderProps
> = ({ children, bundleDefinition }) => (
    <BundlerProvider bundleDefinition={bundleDefinition}>
        {children}
    </BundlerProvider>
);
